import {
  ArrowDown,
  ArrowUp,
  Camera,
  FileText,
  Image as ImageIcon,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { useRef, useState, type DragEvent, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  buildA4Pdf,
  imageFileToPage,
  isImageFile,
  isPdfFile,
  MAX_DOCUMENT_BYTES,
  readPdfPages,
  type PageImage,
} from './document';
import { PageEditorDialog } from './PageEditorDialog';
import { PdfPreview } from './PdfPreview';

/** What the expense's document will be once saved. */
export type DocumentChoice =
  | { readonly kind: 'unset' }
  /** Edit mode: the stored document stays. */
  | { readonly kind: 'existing'; readonly pages: number }
  | {
      readonly kind: 'pdf';
      readonly pdf: Blob;
      readonly source: 'images' | 'pdf';
      readonly pages: number;
    }
  | { readonly kind: 'awaited'; readonly note: string }
  | { readonly kind: 'not-required' };

export type DocumentStage = 'document' | 'pages';

export type FileProblem = {
  readonly kind: 'unusable' | 'tooLarge';
  readonly name: string;
} | null;

type InputRef = RefObject<HTMLInputElement | null>;
export type FileInputRefs = {
  readonly camera: InputRef;
  readonly gallery: InputRef;
  readonly pdf: InputRef;
  readonly any: InputRef;
};

export type TakenFiles = {
  readonly problem: FileProblem;
  readonly pdf: { readonly blob: Blob; readonly pages: number } | null;
  readonly added: readonly PageImage[];
};

export function useFileInputRefs(): FileInputRefs {
  const camera = useRef<HTMLInputElement | null>(null);
  const gallery = useRef<HTMLInputElement | null>(null);
  const pdf = useRef<HTMLInputElement | null>(null);
  const any = useRef<HTMLInputElement | null>(null);
  return { camera, gallery, pdf, any };
}

/** The «no document» choice: its state, and the confirmation that turns it into a document choice. */
export function useNoDocument(onChoice: (choice: DocumentChoice) => void, done: () => void) {
  const [noDocument, onNoDocument] = useState<'awaited' | 'not-required' | null>(null);
  const [note, onNote] = useState('');
  const [noteMissing, onNoteMissing] = useState(false);
  const onConfirm = (): void => {
    if (noDocument === 'awaited') {
      if (note.trim() === '') {
        onNoteMissing(true);
        return;
      }
      onChoice({ kind: 'awaited', note: note.trim() });
    } else if (noDocument === 'not-required') {
      onChoice({ kind: 'not-required' });
    }
    done();
  };
  return { noDocument, note, noteMissing, onNoDocument, onNote, onNoteMissing, onConfirm };
}

/** The photo pages of a document in the making, and what can be done with them. */
export function usePageFlow(
  onChoice: (choice: DocumentChoice) => void,
  onStage: (stage: DocumentStage) => void,
  closeChoosing: () => void,
) {
  const [pages, setPages] = useState<readonly PageImage[]>([]);
  const [editing, setEditing] = useState<number | null>(null);
  const [problem, setProblem] = useState<FileProblem>(null);
  const [creating, setCreating] = useState<'idle' | 'working' | 'failed'>('idle');

  const updatePages = (next: readonly PageImage[]): void => {
    setPages(next);
    onStage(next.length > 0 ? 'pages' : 'document');
  };

  const takeFiles = async (files: readonly File[]): Promise<void> => {
    setProblem(null);
    if (files.length === 0) return;
    const taken = await readFiles(files, pages.length > 0);
    setProblem(taken.problem);
    if (taken.pdf !== null) {
      closeChoosing();
      onChoice({ kind: 'pdf', pdf: taken.pdf.blob, source: 'pdf', pages: taken.pdf.pages });
    } else if (taken.added.length > 0) {
      updatePages([...pages, ...taken.added]);
    }
  };

  const pick = (files: FileList | null): void => {
    void takeFiles(files ? Array.from(files) : []);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    void takeFiles(Array.from(event.dataTransfer.files));
  };

  const move = (index: number, by: -1 | 1): void => {
    const next = [...pages];
    const [page] = next.splice(index, 1);
    next.splice(index + by, 0, page!);
    updatePages(next);
  };

  const createPdf = async (): Promise<void> => {
    setCreating('working');
    try {
      const pdf = await buildA4Pdf(pages);
      setCreating('idle');
      const count = pages.length;
      updatePages([]);
      closeChoosing();
      onChoice({ kind: 'pdf', pdf, source: 'images', pages: count });
    } catch {
      setCreating('failed');
    }
  };
  return {
    pages,
    editing,
    problem,
    creating,
    setEditing,
    updatePages,
    pick,
    onDrop,
    move,
    createPdf,
  };
}

/** What the dropped or picked files come to: one PDF as a whole, or photos as pages. */
export async function readFiles(files: readonly File[], hasPages: boolean): Promise<TakenFiles> {
  const pdf = files.find(isPdfFile);
  if (pdf && !hasPages) {
    if (pdf.size > MAX_DOCUMENT_BYTES) {
      return { problem: { kind: 'tooLarge', name: pdf.name }, pdf: null, added: [] };
    }
    try {
      const count = await readPdfPages(pdf);
      return {
        problem: null,
        pdf: { blob: new Blob([pdf], { type: 'application/pdf' }), pages: count },
        added: [],
      };
    } catch {
      return { problem: { kind: 'unusable', name: pdf.name }, pdf: null, added: [] };
    }
  }
  let problem: FileProblem = null;
  const added: PageImage[] = [];
  for (const file of files) {
    if (!isImageFile(file)) {
      problem = { kind: 'unusable', name: file.name };
      continue;
    }
    try {
      added.push(await imageFileToPage(file));
    } catch {
      problem = { kind: 'unusable', name: file.name };
    }
  }
  return { problem, pdf: null, added };
}

export function HiddenFileInputs({
  refs,
  pick,
}: {
  readonly refs: FileInputRefs;
  readonly pick: (files: FileList | null) => void;
}) {
  return (
    <>
      <input
        ref={refs.camera}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="capture-camera-input"
        onChange={(e) => {
          pick(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={refs.gallery}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        className="hidden"
        data-testid="capture-gallery-input"
        onChange={(e) => {
          pick(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={refs.pdf}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        data-testid="capture-pdf-input"
        onChange={(e) => {
          pick(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={refs.any}
        type="file"
        accept="image/*,.heic,.heif,application/pdf,.pdf"
        multiple
        className="hidden"
        data-testid="capture-any-input"
        onChange={(e) => {
          pick(e.target.files);
          e.target.value = '';
        }}
      />
    </>
  );
}

function PageCard({
  page,
  index,
  count,
  onEdit,
  onMove,
  onRemove,
}: {
  readonly page: PageImage;
  readonly index: number;
  readonly count: number;
  readonly onEdit: (index: number) => void;
  readonly onMove: (index: number, by: -1 | 1) => void;
  readonly onRemove: (index: number) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <li className="space-y-1 rounded-lg border border-border p-2">
      <button
        type="button"
        className="block w-full"
        aria-label={t('capture.pages.edit', { n: index + 1 })}
        onClick={() => onEdit(index)}
      >
        <img
          src={page.dataUrl}
          alt=""
          className="mx-auto block aspect-[3/4] w-full rounded object-contain"
        />
      </button>
      <div className="flex items-center justify-between gap-1">
        <span className="text-xs text-muted-foreground">
          {t('capture.pages.page', { n: index + 1 })}
        </span>
        <span className="flex">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('capture.pages.edit', { n: index + 1 })}
            onClick={() => onEdit(index)}
          >
            <Pencil />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={index === 0}
            aria-label={t('capture.pages.moveUp', { n: index + 1 })}
            onClick={() => onMove(index, -1)}
          >
            <ArrowUp />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={index === count - 1}
            aria-label={t('capture.pages.moveDown', { n: index + 1 })}
            onClick={() => onMove(index, 1)}
          >
            <ArrowDown />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('capture.pages.remove', { n: index + 1 })}
            onClick={() => onRemove(index)}
          >
            <Trash2 />
          </Button>
        </span>
      </div>
    </li>
  );
}

export function PagesStep({
  uid,
  inputs,
  pages,
  problem,
  creating,
  editing,
  ...on
}: {
  readonly uid: string;
  readonly inputs: ReactNode;
  readonly pages: readonly PageImage[];
  readonly problem: FileProblem;
  readonly creating: 'idle' | 'working' | 'failed';
  readonly editing: number | null;
  readonly onEdit: (index: number) => void;
  readonly onMove: (index: number, by: -1 | 1) => void;
  readonly onRemove: (index: number) => void;
  readonly onAdd: () => void;
  readonly onCreate: () => void;
  readonly onCancel: () => void;
  readonly onCloseEditor: () => void;
  readonly onPageDone: (page: PageImage) => void;
}) {
  const { t } = useTranslation('expenses');
  const { onEdit, onMove, onRemove, onAdd, onCreate, onCancel, onCloseEditor, onPageDone } = on;
  return (
    <section aria-labelledby={`${uid}-pages`} className="space-y-4">
      {inputs}
      <h2 id={`${uid}-pages`} className="text-base font-semibold">
        {t('capture.pages.heading', { count: pages.length })}
      </h2>
      <ol className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {pages.map((page, index) => (
          <PageCard
            key={page.id}
            page={page}
            index={index}
            count={pages.length}
            {...{ onEdit, onMove, onRemove }}
          />
        ))}
        <li>
          <button
            type="button"
            onClick={() => onAdd()}
            className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-1 rounded-lg
          border border-dashed border-border text-sm text-muted-foreground"
          >
            <Plus aria-hidden="true" />
            {t('capture.pages.add')}
          </button>
        </li>
      </ol>
      <p className="text-sm text-muted-foreground">{t('capture.pages.hint')}</p>
      {problem && <FileProblemNote problem={problem} />}
      {creating === 'failed' && (
        <p role="alert" className="text-sm text-destructive">
          {t('capture.pages.createFailed')}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" disabled={creating === 'working'} onClick={() => onCreate()}>
          {creating === 'working' ? t('capture.pages.creating') : t('capture.pages.createPdf')}
        </Button>
        <Button type="button" variant="outline" onClick={() => onCancel()}>
          {t('capture.pages.cancel')}
        </Button>
      </div>
      {editing !== null && pages[editing] && (
        <PageEditorDialog
          page={pages[editing]}
          index={editing}
          onCancel={onCloseEditor}
          onDone={onPageDone}
        />
      )}
    </section>
  );
}

export function ChosenStep({
  uid,
  choice,
  replaceable,
  onChange,
  onContinue,
}: {
  readonly uid: string;
  readonly choice: Exclude<DocumentChoice, { kind: 'unset' }>;
  readonly replaceable: boolean;
  readonly onChange: () => void;
  readonly onContinue: () => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <section aria-labelledby={`${uid}-doc`} className="space-y-3">
      <h2 id={`${uid}-doc`} className="text-base font-semibold">
        {t('capture.document.heading')}
      </h2>
      {choice.kind === 'pdf' && (
        <>
          <PdfPreview pdf={choice.pdf} pages={choice.pages} />
          <p className="text-sm text-muted-foreground">
            {t('capture.document.ready', { count: choice.pages })}
          </p>
        </>
      )}
      {choice.kind === 'existing' && (
        <p className="text-sm">
          <FileText aria-hidden="true" className="mr-1 inline size-4" />
          {t('capture.document.attached', { count: choice.pages })}
        </p>
      )}
      {choice.kind === 'awaited' && (
        <p className="text-sm">{t('capture.document.choiceAwaited', { note: choice.note })}</p>
      )}
      {choice.kind === 'not-required' && (
        <p className="text-sm">{t('capture.document.choiceNotRequired')}</p>
      )}
      <Button type="button" variant="outline" onClick={() => onChange()}>
        {replaceable || choice.kind === 'pdf' || choice.kind === 'existing'
          ? t('capture.document.replace')
          : t('capture.document.change')}
      </Button>
      <Button type="button" className="ml-2 lg:hidden" onClick={onContinue}>
        {t('capture.document.toFields')}
      </Button>
    </section>
  );
}

function SourceButtons({ refs }: { readonly refs: FileInputRefs }) {
  const { t } = useTranslation('expenses');
  const sources = [
    { key: 'camera', icon: Camera, ref: refs.camera },
    { key: 'gallery', icon: ImageIcon, ref: refs.gallery },
    { key: 'pdf', icon: FileText, ref: refs.pdf },
  ] as const;
  return (
    <div className="grid gap-2 lg:hidden" data-testid="capture-sources">
      {sources.map(({ key, icon: Icon, ref }) => {
        const primary = key === 'camera';
        return (
          <button
            key={key}
            type="button"
            onClick={() => ref.current?.click()}
            className={cn(
              `flex items-center gap-3 rounded-lg border px-4 text-left focus-visible:ring-[3px]
              focus-visible:ring-ring focus-visible:outline-none`,
              primary
                ? 'min-h-16 border-primary bg-primary py-4 text-primary-foreground hover:bg-primary/90'
                : 'min-h-12 border-border bg-card py-2 hover:bg-muted',
            )}
          >
            <Icon
              aria-hidden="true"
              className={cn('shrink-0', primary ? 'size-6' : 'size-5 text-brand')}
            />
            <span>
              <span className={cn('block', primary ? 'font-semibold' : 'font-medium')}>
                {t(`capture.document.${key}`)}
              </span>
              <span
                className={cn(
                  'block text-sm',
                  primary ? 'text-primary-foreground/80' : 'text-muted-foreground',
                )}
              >
                {t(`capture.document.${key}Hint`)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function DropZone({
  dragging,
  anyRef,
  onDrag,
  onDrop,
}: {
  readonly dragging: boolean;
  readonly anyRef: InputRef;
  readonly onDrag: (dragging: boolean) => void;
  readonly onDrop: (event: DragEvent<HTMLDivElement>) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <div
      data-testid="capture-drop-zone"
      onDragOver={(event) => {
        event.preventDefault();
        onDrag(true);
      }}
      onDragLeave={() => onDrag(false)}
      onDrop={(event) => {
        onDrag(false);
        onDrop(event);
      }}
      className={cn(
        `hidden min-h-48 flex-col items-center justify-center gap-2 rounded-lg border-2
        border-dashed p-6 text-center text-sm text-muted-foreground lg:flex`,
        dragging ? 'border-brand bg-selected' : 'border-border',
      )}
    >
      <Upload aria-hidden="true" className="size-6 text-brand" />
      <p>
        {t('capture.document.drop')}{' '}
        {/* The link and its full stop wrap together, never the «.» alone (uiux's nit). */}
        <span className="whitespace-nowrap">
          <button
            type="button"
            className="font-medium text-foreground underline"
            onClick={() => anyRef.current?.click()}
          >
            {t('capture.document.chooseFiles')}
          </button>
          .
        </span>
      </p>
    </div>
  );
}

function NoDocumentFieldset({
  uid,
  noDocument,
  note,
  noteMissing,
  onNoDocument,
  onNote,
  onNoteMissing,
  onConfirm,
}: {
  readonly uid: string;
  readonly noDocument: 'awaited' | 'not-required' | null;
  readonly note: string;
  readonly noteMissing: boolean;
  readonly onNoDocument: (kind: 'awaited' | 'not-required') => void;
  readonly onNote: (note: string) => void;
  readonly onNoteMissing: (missing: boolean) => void;
  readonly onConfirm: () => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <fieldset className="space-y-2 border-t border-border pt-4">
      <legend className="text-sm font-semibold">{t('capture.document.noDocument')}</legend>
      <p className="text-sm text-muted-foreground">{t('capture.document.noDocumentHint')}</p>
      {(['awaited', 'not-required'] as const).map((kind) => {
        const key = kind === 'awaited' ? 'awaited' : 'notRequired';
        return (
          <label key={kind} className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name={`${uid}-none`}
              className="mt-1"
              checked={noDocument === kind}
              onChange={() => {
                onNoDocument(kind);
                onNoteMissing(false);
              }}
            />
            <span>
              <span className="block font-medium">{t(`capture.document.${key}`)}</span>
              <span className="block text-muted-foreground">
                {t(`capture.document.${key}Hint`)}
              </span>
            </span>
          </label>
        );
      })}
      {noDocument === 'awaited' && (
        <div className="space-y-1 pl-6">
          <label htmlFor={`${uid}-note`} className="text-sm font-medium">
            {t('capture.document.awaitedNote')}
          </label>
          <Input
            id={`${uid}-note`}
            value={note}
            maxLength={200}
            placeholder={t('capture.document.awaitedNotePlaceholder')}
            aria-invalid={noteMissing}
            aria-describedby={noteMissing ? `${uid}-note-error` : undefined}
            onChange={(e) => {
              onNote(e.target.value);
              onNoteMissing(false);
            }}
          />
          {noteMissing && (
            <p id={`${uid}-note-error`} role="alert" className="text-sm text-destructive">
              {t('capture.document.awaitedNoteRequired')}
            </p>
          )}
        </div>
      )}
      {noDocument !== null && (
        <Button type="button" variant="outline" onClick={onConfirm}>
          {t('capture.document.continueWithout')}
        </Button>
      )}
    </fieldset>
  );
}

export function SourcesStep(props: {
  readonly uid: string;
  readonly inputs: ReactNode;
  readonly refs: FileInputRefs;
  readonly problem: FileProblem;
  readonly choosing: boolean;
  readonly dragging: boolean;
  readonly noDocument: 'awaited' | 'not-required' | null;
  readonly note: string;
  readonly noteMissing: boolean;
  readonly onDrag: (dragging: boolean) => void;
  readonly onDrop: (event: DragEvent<HTMLDivElement>) => void;
  readonly onNoDocument: (kind: 'awaited' | 'not-required') => void;
  readonly onNote: (note: string) => void;
  readonly onNoteMissing: (missing: boolean) => void;
  readonly onConfirm: () => void;
  readonly onKeep: () => void;
}) {
  const { t } = useTranslation('expenses');
  const { uid, inputs, refs, problem, choosing, dragging, onDrag, onDrop, onKeep } = props;
  return (
    <section aria-labelledby={`${uid}-doc`} className="space-y-4">
      {inputs}
      <h2 id={`${uid}-doc`} className="text-base font-semibold">
        {t('capture.document.heading')}
      </h2>
      {/* Phone: the camera first and prominent, gallery and PDF beside it (the board, step 1). */}
      <SourceButtons refs={refs} />
      {/* Computer: the pane is a drop zone; «scegli i file» opens the picker (images and PDF). */}
      <DropZone dragging={dragging} anyRef={refs.any} onDrag={onDrag} onDrop={onDrop} />
      {problem && <FileProblemNote problem={problem} />}
      <NoDocumentFieldset {...props} />
      {choosing && (
        <Button type="button" variant="ghost" onClick={onKeep}>
          {t('capture.document.keep')}
        </Button>
      )}
    </section>
  );
}

export function FileProblemNote({ problem }: { readonly problem: NonNullable<FileProblem> }) {
  const { t } = useTranslation('expenses');
  return (
    <div role="alert" className="rounded-lg border border-destructive/40 p-3 text-sm">
      <p className="font-semibold text-destructive">{t('capture.document.unusable')}</p>
      <p>
        {problem.kind === 'tooLarge'
          ? t('capture.document.tooLarge', { name: problem.name })
          : t('capture.document.unusableBody', { name: problem.name })}
      </p>
    </div>
  );
}
